<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from '@/Partials/Atoms/Button/Button.vue';
import { useContextStore } from '@/Stores/context';

interface DeferredInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_STORAGE_KEY = 'done-pwa-install-prompt-dismissed';

const { t } = useI18n();
const contextStore = useContextStore();

const deferredPrompt = ref<DeferredInstallPromptEvent | null>(null);
const isVisible = ref(false);
const isIosDevice = ref(false);

const canInstallDirectly = computed(() => Boolean(deferredPrompt.value));
const showManualHint = computed(() => !canInstallDirectly.value);

function isInStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

function isMobileDevice(): boolean {
  const ua = window.navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod/.test(ua);
}

function detectIosDevice(): boolean {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
}

function shouldShowPrompt(): boolean {
  if (!isMobileDevice()) return false;
  if (contextStore.isKite) return false;
  if (isInStandaloneMode()) return false;
  if (localStorage.getItem(DISMISS_STORAGE_KEY) === '1') return false;
  return true;
}

function dismissPrompt(remember = true): void {
  isVisible.value = false;
  if (remember) {
    localStorage.setItem(DISMISS_STORAGE_KEY, '1');
  }
}

async function installPwa(): Promise<void> {
  if (!deferredPrompt.value) {
    return;
  }

  const installEvent = deferredPrompt.value;
  await installEvent.prompt();
  const choice = await installEvent.userChoice;
  deferredPrompt.value = null;

  if (choice.outcome === 'accepted') {
    dismissPrompt(true);
  }
}

function onBeforeInstallPrompt(event: Event): void {
  if (!shouldShowPrompt()) {
    return;
  }

  const installEvent = event as DeferredInstallPromptEvent;
  installEvent.preventDefault();

  deferredPrompt.value = installEvent;
  isVisible.value = true;
}

function onAppInstalled(): void {
  dismissPrompt(true);
}

onMounted(() => {
  if (!shouldShowPrompt()) {
    return;
  }

  isIosDevice.value = detectIosDevice();
  if (isIosDevice.value) {
    isVisible.value = true;
  }

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.removeEventListener('appinstalled', onAppInstalled);
});
</script>

<template>
  <Transition name="pwa-install-fade">
    <div v-if="isVisible" class="pwa-install" role="dialog" aria-modal="true">
      <div class="pwa-install__backdrop" @click="dismissPrompt(true)"></div>
      <div class="pwa-install__card">
        <div class="pwa-install__close">
          <Button
            modifier="round"
            icon="close"
            :aria-label="t('general.pwaInstall.close')"
            @click="dismissPrompt(true)"
          />
        </div>
        <div>
          <p class="pwa-install__title">{{ t('general.pwaInstall.title') }}</p>
          <p class="pwa-install__text">{{ t('general.pwaInstall.description') }}</p>
          <p v-if="showManualHint" class="pwa-install__hint">
            {{ t('general.pwaInstall.iosHint') }}
          </p>
        </div>
        <div class="pwa-install__action">
          <Button
            v-if="canInstallDirectly"
            modifier="conversion"
            :text="t('general.pwaInstall.install')"
            @click="installPwa"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
@import '@/Scss/PwaInstall/Component/pwa-install';
</style>
